<template>
  <div class="d-flex" style="min-height: 45vw;">
    <div class="content p-4 w-100">
      <div class="d-flex justify-content-between mb-1">
        <div class="text-headline-small card-title mt-0">
          {{ i18n.global.t('pages.accounts.title.list') }}
        </div>
      </div>
      <div class="mb-5 d-flex justify-end">
        <div class="btn-area">
          <v-btn v-show="createAuthority && selfRoleId == 1" class="exec-btn mt-0" @click="moveToAccountForm">
            {{ i18n.global.t('pages.common.button.newCreate') }}
          </v-btn>
          <FileDownloadBtn
            :showable="csvtAuthorities.csvExport"
            btnClass="exec-btn mt-0 ml-1"
            :btnName="i18n.global.t('pages.common.button.csvExport')"
            :type="downloadType"
            :filterItems="searchParams"
          />
          <v-btn
            v-show="csvtAuthorities.csvImport"
            class="exec-btn mt-0 ml-1"
            @click="activateModalState"
          >
            {{ i18n.global.t('pages.common.button.csvImport') }}
          </v-btn>
        </div>
        <UpsertCsvButtons
          v-if="csvtAuthorities.csvImport || csvtAuthorities.csvExport"
          :title="$t('pages.common.button.csvUpsert')"
          :importTitle="$t('pages.accounts.csv.importTitle')"
          :importType="importType"
          :exportType="exportType"
          :filterItems="searchParams"
          :getList="getAccounts"
          :authorities="csvtAuthorities"
          elementId="accounts-upsert"
        />
      </div>
      <div>
        <AccountList
          class="mb-2"
          :selfRoleId="selfRoleId"
          :accounts="accounts"
          :deletable="deletable"
          :listLoading="listLoading"
          :changeStatusAuthority="changeStatusAuthority"
          :updatingStatus="updatingStatus"
          @changeStatus="updateStatus"
          @changeSortOrder="changeSortOrder"
          :showFlash="showFlash"
          :reload="getAccounts"
          :isOperationOffice="isOperationOffice"
          :isManagementOffice="isManagementOffice"
          :isSearchCompleted="isSearchCompleted"
        />
        <PaginateWithCount
          class="justify-end"
          :totalPage="totalPage"
          v-model="currentPage"
          :itemsPerPage="itemsPerPage"
          :allElementsCount="allElementsCount"
          :showItemCount="true"
        />
        <hr />
      </div>
    </div>

    <AccountsSearchForm
      :loading="listLoading"
      @search="getAccountsAfterSetParams"
      @clear="clearSearchParams"
    />

    <FileUploadModal
      :modalState="modalState"
      :title="i18n.global.t('pages.accounts.csv.importTitle')"
      uploadBtnClass='exec-btn mt-0 ml-1'
      closeBtnClass='mt-0'
      :type="uploadType"
      templateCsvFileType="accounts"
      @closeModal="inactivateModalState"
    >
      <template v-slot:supplementary-message>
        <div v-if="isOperationOffice || isDepartment">
          {{ i18n.global.t('pages.accounts.csv.currentLoginId', {operationOfficeId: loginOrganizationId}) }}
        </div>
      </template>
    </FileUploadModal>
  </div>
</template>

<script>
  import AccountList from "@/components/AccountList"
  import AccountsSearchForm from "@/components/AccountsSearchForm"
  import FileDownloadBtn from "@/components/Files/DownloadButton"
  import FileUploadModal from "@/components/Files/UploadModal"
  import Api from "@/services/api/ApiServiceFabrick";
  import PaginateWithCount from "@/components/PaginateWithCount.vue"
  import AuthorityCacheApi from "@/services/models/AuthorityCacheApi"
  import { useAuth } from "@/services/user/Auth0UserProvider"
  import UpsertCsvButtons from "@/components/Common/UpsertCsvButtons"
  import { MANAGEMENT_OFFICE_TYPE, OPERATION_OFFICE_TYPE, DEPARTMENT_OFFICE_TYPE } from "@/services/consts"
  import i18n from '@/lang/i18n';
  import { saveDataToSessionStorage, loadDataFromSessionStorage, clearSessionStorage } from "@/services/functions/sessionStorage";

  export default {
    props: {
      showFlash: {
        type: Function
      }
    },
    data() {
      return {
        selfRoleId: 2,
        currentPage: 1,
        totalPage: 0,
        itemsPerPage: 50,
        allElementsCount: 0,
        accounts: [],
        listLoading: false,
        isSearchCompleted: false,
        searchParams: {
          page: 1,
          withs: 'updatedBy',
          status: 'only-active'
        },
        updatingStatus: false,
        creatingOrUpdating: false,
        editAccountId: null,
        modalFormText: null,
        errors: {},
        downloadType: "App\\Services\\Csv\\Exports\\AccountService",
        uploadType: "App\\Services\\Csv\\Imports\\AccountService",
        modalState: false,
        officeAutocompleteStatus: false,
        importType: "UpsertImportAccount",
        exportType: "UpsertExportAccount",
        createAuthority: false,
        changeStatusAuthority: false,
        csvtAuthorities: {
          csvImport: false,
          csvExport: false
        },
        isManagementOffice: false,
        isOperationOffice: false,
        isDepartment: false,
        loginOrganizationId: null,
      }
    },
    setup() {
      document.title = i18n.global.t('pages.accounts.title.list');
      const { loginWithRedirect } = useAuth();

      return {
        loginWithRedirect,
        i18n
      }
    },
    computed: {
      officeAutoCompleteHeight() {
        return this.officeAutocompleteStatus ? " min-height: 38vh;" : ""
      },
      deletable() {
        return this.changeStatusAuthority && this.isManagementOffice;
      }
    },
    watch: {
      currentPage: {
        handler() {
          this.searchParams.page = this.currentPage;
          this.getAccounts()
        }
      },
    },
    mounted() {
      this.listLoading = true;
      this.getAuthorities()
      const savedSearchParams = loadDataFromSessionStorage('accountSearchParams');
      if (savedSearchParams) {
        this.searchParams = savedSearchParams;
      }
      Api().getSelfAccount().then((data) => {
        this.selfRoleId = data.role_id;
        this.isManagementOffice = data.login_organization_type == MANAGEMENT_OFFICE_TYPE
        this.isOperationOffice = data.login_organization_type == OPERATION_OFFICE_TYPE
        this.isDepartment = data.login_organization_type == DEPARTMENT_OFFICE_TYPE
        this.loginOrganizationId = data.officeable_id;
        if (this.isManagementOffice && !savedSearchParams) {
          this.listLoading = false;
          return;
        }
        this.getAccounts();
      }).catch((error) => {
        const res = error.response
        if (res.status == 401) {
          this.loginWithRedirect({ appState: { targetUrl: location.pathname } });
        }
        this.listLoading = false;
      })
    },
    methods: {
      async getAuthorities(isError = false) {
        if (isError) {
          await AuthorityCacheApi.reflesh()
        }
        const authorityList = await AuthorityCacheApi.getWithParse();
        this.createAuthority = !!authorityList["octlink.user.create"];
        this.changeStatusAuthority = !!authorityList["octlink.user.delete"];
        this.csvtAuthorities.csvImport = !!authorityList["octlink.user.input"];
        this.csvtAuthorities.csvExport = !!authorityList["octlink.user.output"];
      },
      getAccounts() {
        this.listLoading = true;

        Api().getPaginatedAccounts(this.searchParams).then((data) => {
          this.accounts = data.accounts
          this.currentPage = data.current_page
          this.totalPage = data.total_page
          this.allElementsCount = data.total
          this.itemsPerPage = data.per_page
          this.listLoading = false
          this.isSearchCompleted = true;
        }).catch((error) => {
          const res = error.response
          switch (res.status) {
            case 401:
              this.loginWithRedirect({ appState: { targetUrl: location.pathname } })
              break
            case 403:
              location.reload()
              break
            case 422:
              this.showFlash(i18n.global.t('pages.accounts.messages.getListError'), "error", false)
          }
          this.listLoading = false;
        })
      },
      updateStatus(params) {
        this.updatingStatus = true;
        Api().updateAccountStatus(params).then((_data) => {
          this.showFlash(this.$t("pages.common.messages.changeStatusSuccess"), "success");
          this.updatingStatus = false;
          this.getAccounts();
        }).catch((error) => {
          const res = error.response
          switch (res.status) {
            case 401:
              this.loginWithRedirect({ appState: { targetUrl: location.pathname } })
              break
            case 403:
              this.showFlash(this.$t("pages.common.messages.forbidden"), "error", false)
              this.getAuthorities(true)
              break
            case 422:
              this.showFlash(this.$t("pages.common.messages.changeStatusFail"), "error", false)
          }
          this.updatingStatus = false;
        })
      },
      getAccountsAfterSetParams(searchParams) {
        searchParams.page = 1;
        searchParams.withs = this.searchParams.withs;
        searchParams.sort = this.searchParams.sort;
        searchParams.sort_type = this.searchParams.sort_type;
        this.searchParams = searchParams;
        saveDataToSessionStorage('accountSearchParams', this.searchParams)
        this.getAccounts();
      },
      changeSortOrder(sortItem, sortType) {
        this.searchParams.sort = sortItem;
        this.searchParams.sort_type = sortType;
        if (this.currentPage == 1) {
          this.getAccounts();
        } else {
          this.currentPage = 1;
        }
      },
      moveToAccountForm() {
        this.$router.push('/accounts/form/');
      },
      inactivateModalState(isReacquireAccount, key = null) {
        this.modalState = false;
        if (isReacquireAccount) {
          this.getAccounts();
        }
      },
      activateModalState() {
        this.modalState = true;
      },
      clearSearchParams() {
        clearSessionStorage('accountSearchParams');
      },
    },
    components: {
      AccountList,
      AccountsSearchForm,
      PaginateWithCount,
      FileDownloadBtn,
      FileUploadModal,
      UpsertCsvButtons,
    }
  }
</script>
